
import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation  } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';


import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle';
import QueueIcon from '@mui/icons-material/Queue';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

import BackpackIcon from '@mui/icons-material/Backpack';

import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';


import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, isSearchBoxShowAtom, isSaveLoadingAtom, currentUserSessionAtom } from 'library/store';
import useAdvanceOnSalaryService, { useBasicFilterAdvanceOnSalary } from './services/AdvanceOnSalary';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Chip, Link, FormHelperText } from '@mui/material';
import NumberFormat from 'react-number-format';

import { IAdvanceOnSalary,  defaultAdvanceOnSalary } from './models/AdvanceOnSalary';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';

import useUtils from 'library/utils';

import IEnumeration, { IEnumerationItem,  Enum_OPERATION_STATUS,
      Enum_CORPORATION_TYPE, Enum_ADVANCE_ON_SALARY_STATUS_PURPOSE, 
      Enum_ADVANCE_ON_SALARY_TYPE} from 'features/configuration/models/Enumeration';

import { IExtensionType  } from 'features/configuration/models/ExtensionType';
import useExtensionTypeService, { useBasicFilterExtensionType } from 'features/configuration/services/ExtensionType';

import { justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';

import { useBasicFilterEmployee } from 'features/setup/services/Employee';
import { IEmployee } from 'features/setup/models/Employee';
import { DatePicker } from '@mui/x-date-pickers';
import { BasicPersonForm } from './BasicPersonForm';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';



export const AdvanceOnSalaryForm: FC<IAdvanceOnSalary> = (props: IAdvanceOnSalary = defaultAdvanceOnSalary) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();  
  const { enqueueSnackbar } = useSnackbar();

  const {id} = useParams();
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const { language: lg, applicationSetup} = useRecoilValue(currentUserSessionAtom);

  const {retrieveEntity, retrieveData, openEntityActionDrawer, 
      checkEntityExpressionSyntax, checkEntitySaveAuthorization } = entityService();

  const {getEnumerationItemsByEnumerationCodes, getAsOptions } = useEnumerationService();

  const { createAdvanceOnSalary, updateAdvanceOnSalary } = useAdvanceOnSalaryService();

  const {capitalizeFirstLetter} = useUtils();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);
  
  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterAdvanceOnSalary = useBasicFilterAdvanceOnSalary( 
    (event: React.MouseEvent<unknown>, row: IAdvanceOnSalary) => {
        setIsSearchBoxShow(false);
        _setId(row.id);
      }
  );


  const {canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();

  const [openEmployeeFilter, setOpenEmployeeFilter] = useState(false);
  const basicFilterEmployee = useBasicFilterEmployee( 
      async (event: React.MouseEvent<unknown>, row: IEmployee) => {
        const {id, personId, registrationNumber, status } = row;        
        
        setValue('employeeId', id);
        setValue('personId', personId);

        setOpenEmployeeFilter(false);

        const employee = await retrieveEntity('Employee', id);

        setValue('employee', employee);

        setValue('employeeRegistrationNumber', registrationNumber);
        
        setValue('employeeStatus', status); 
      }
  );

  const emptyFunc = (obj: any) => {}
  
  const methods = useForm<IAdvanceOnSalary>({defaultValues: {...defaultAdvanceOnSalary}});

  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  const watchPersonId = watch('personId');
  const watchAdvanceType = watch('advanceType');
  

  // const watchParentProductId = watch('parentProductId');

  // let { fields, append: appendCoverages, update: updateCoverage ,remove: removeCoverage,  } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `productCoverages`,
  //   control,            
  // });
  

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IAdvanceOnSalary>,Error,IAdvanceOnSalary>(
      _id>0?updateAdvanceOnSalary:createAdvanceOnSalary, {   
        onSuccess: (data: IResult<IAdvanceOnSalary>) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          setIsSaveLoading(false);
          _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);
          
          queryClient.invalidateQueries(['AdvanceOnSalary',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });

    const {data: _data, refetch} = useQuery<IAdvanceOnSalary>(['AdvanceOnSalary', _id], () => retrieveEntity('AdvanceOnSalary',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

      const {data: enumItems, refetch: refetchEnumerationItems} = useQuery<IEnumerationItem[]>(
        ['EnumerationItems', 'AdvanceOnSalary'], () => 
          getEnumerationItemsByEnumerationCodes( [Enum_CORPORATION_TYPE, 
              Enum_ADVANCE_ON_SALARY_STATUS_PURPOSE, Enum_OPERATION_STATUS, Enum_ADVANCE_ON_SALARY_TYPE ] ));         

    const handleClickOpenEmployee = async (event: any) => {          
      setOpenEmployeeFilter(true);
    }

    const refEnumItems = useRef<IEnumerationItem[]>();    
    useEffect( () => {   
        refEnumItems.current = enumItems;
      
    }, [enumItems])

      useEffect( () => {        
        setCurrentFormNameAtom(`${t('Advance on salary')}`);
        setCurrentBasicTextFilterProps(basicFilterAdvanceOnSalary);
      }, []);    
    
      /********** This use effect call retreive data wich will call refetch and _data will be updated. 
        and the new useEffect will take place ********************/
        useEffect( () => {
            // setCurrentFormName(t('Billing'));        
            
            if(_id > 0)
              retrieveData('AdvanceOnSalary',_id, refetch);  
          }, [_id] );
    
    
        useEffect( () => {
            
        if(_data && _data.id > 0) {
            reset(_data);
        }
        }, [_data]);
    
      const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
        _setId(0);      
        reset(defaultAdvanceOnSalary);    
      }
      
      const saveData = async (event: MouseEvent<HTMLButtonElement>) => {      
        if(!checkEntitySaveAuthorization('AdvanceOnSalary', _id)) {
          setIsSaveLoading(false);
             return;
        }          
  
        const data = getValues(); 

                
        if(data.employeeId <= 0) {
            enqueueSnackbar( t('Reference is not specified'), { variant: 'warning',
              anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
            setIsSaveLoading(false);
            return;
          }

       
    
        mutate(data);
      }
    
      const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
        openEntityActionDrawer('AdvanceOnSalary', _id);
      }

    
      
    const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
    }

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={0.5} columnSpacing={0.1}>
                    <Grid item xs={12} md={7} lg={7} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                      <Stack flexDirection='column'  >
                             
                                                    
                                                  
                          <Box sx={{ mt: 1.25, width: '100%' }} > 
                            <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                  sx={{...typographyGroupBoxStyling}}>
                              {`${t(('Employee identification'))} `}
                            </Typography>                                                       
                          </Box> 
                          <Box sx={{ mt: 1, width: '100%' }} key={` person ${getValues().personId}`}>
                            <TextField sx={{width:'calc(20% - 8px)'}} id="employeeRegistrationNumber" 
                              label={t('Matricule')} {...register('employeeRegistrationNumber')} 
                              InputProps={{                                      
                                endAdornment: (
                                  <InputAdornment position="end">  
                                    <IconButton color="primary" onClick={handleClickOpenEmployee} >
                                      <ArrowDropDownCircleIcon />
                                    </IconButton>                                                                                                                                    
                                  </InputAdornment>
                              ) 
                            }}/>  
                            
                            <Controller name='employeeStatus' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="employeeStatus"
                                    label={t('Status')} inputProps={ {readOnly: true}} >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_OPERATION_STATUS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                              />    
                              <Controller name='employeeStatus' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="employeeStatus"
                                    label={t('Status')} inputProps={ {readOnly: true}} >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_OPERATION_STATUS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                              />
                              <Controller name='employeeStatus' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="employeeStatus"
                                    label={t('Status')} inputProps={ {readOnly: true}} >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_OPERATION_STATUS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                              />              
                              { openEmployeeFilter && <FormDialog open={openEmployeeFilter} maxWidth='md'
                                okText='' cancelText='' title={t('Employee')} onCancel={()=> {}} 
                                onClose={()=> {setOpenEmployeeFilter(false);}} onOk={()=> {setOpenEmployeeFilter(false);}}  >
                                    <BasicTextFilterForm<IEmployee> {...basicFilterEmployee } />
                            </FormDialog> }
                          </Box>
                          <BasicPersonForm 
                            {...{personKeyPath: `personId`, personPath: `employee.person`, isIdentityOnly: true, isReadonly:true,
                              control, getValues, setValue, watch }}  />
                          <Box sx={{ mt: 1.25, width: '100%' }} > 
                            <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                  sx={{...typographyGroupBoxStyling}}>
                              {`${t(('Advance identification'))} `}
                            </Typography>                                                       
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} >
                              <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                              <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                              <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                              <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />

                              
                              <TextField sx={{width:'calc(15% - 8px)'}} id="name" label={t('Code')} {...register('code')} />   
                              
                              <Controller name='status' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="status"
                                    label={t('Status')} inputProps={ {readOnly: true}} >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_OPERATION_STATUS ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                              />
                              <Controller control={control}
                                name='statusDate' 
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                  <DatePicker label={t('Status date')} 
                                    onChange={onChange} disableOpenPicker readOnly                    
                                    value={new Date(value)}
                                        slotProps={{ textField: { sx: {width:'calc(15% - 8px)'}  }} }
                                  /> )}
                              />
                              <Controller name='statusPurpose' control={control}                                     
                                  render={ ({field: {onChange, value}}) => (
                                    <TextField select onChange={onChange} value={value} sx={{width:'calc(30% - 8px)'}} id="statusPurpose"
                                      label={t('Status purpose')} inputProps={ {readOnly: true}} 
                                      >
                                      {enumItems && enumItems.filter( e => 
                                            e.enumerationCode === Enum_ADVANCE_ON_SALARY_STATUS_PURPOSE ).map( 
                                        (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                      }
                                    </TextField>
                                  )}
                              />
                              <Controller control={control}
                                name='issueDate' 
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                  <DatePicker label={t('IssueDate')} 
                                    onChange={onChange} disableOpenPicker readOnly                              
                                    value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(15% - 8px)'}  }} }
                                  /> )}
                              />

                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} > 
                            <Controller name='advanceType' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(40% - 8px)'}} id="advanceType"
                                    label={t('Advance type')} inputProps={ {readOnly: false}} 
                                    helperText={ 
                                      <FormHelperText>                                    
                                        { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_ADVANCE_ON_SALARY_TYPE, 
                                                              t('Advance type'), t('Advance type'))}
                                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                          {t('Add')}
                                        </Link> }
                                        { (canUpdateEnumerationItem && !isFalsy(watchAdvanceType) ) && <Link href="#" onClick={(event) => {
                                               onUpdateItemClick( Enum_ADVANCE_ON_SALARY_TYPE, t('Advance type'), t('Advance type'),
                                                      refEnumItems.current?.find(x => x.enumerationCode === Enum_ADVANCE_ON_SALARY_TYPE && x.code === watchAdvanceType)
                                                    ); }}
                                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                          {t('Update')}
                                        </Link> }
                                      </FormHelperText> }
                                    >
                                    {enumItems && enumItems.filter( e => 
                                          e.enumerationCode === Enum_ADVANCE_ON_SALARY_TYPE ).map( 
                                      (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                    }
                                  </TextField>
                                )}
                            />
                            <TextField sx={{width:'calc(45% - 8px)'}} id="description" label={`${t('Description')}`} 
                              {...register('description')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>   
                            <Controller control={control}
                                name='advanceDate' 
                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                  <DatePicker label={t('Advance date')} 
                                    onChange={onChange}                          
                                    value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(15% - 8px)'}  }} }
                                  /> )}
                              />
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} > 
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Requested amount')} sx={{width:'calc(25% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`requestedAmount`}
                                control={control}
                            />   
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Accepted amount')} sx={{width:'calc(25% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`acceptedAmount`}
                                control={control}
                            /> 
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Accessories')} sx={{width:'calc(25% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`accessories`}
                                control={control}
                            /> 
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Total amount')} sx={{width:'calc(25% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`amount`}
                                control={control}
                            />                            
                          </Box>
                          <Box sx={{ mt: 1.25, width: '100%' }} > 
                            <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                  sx={{...typographyGroupBoxStyling}}>
                              {`${t(('Reimbursement'))} `}
                            </Typography>                                                       
                          </Box>
                          <Box sx={{ mt: 1, width: '100%' }} > 
                            <Controller control={control}
                              name='reimbursementStartDate' 
                              render={({ field: { onChange, onBlur, value, ref } }) => (
                                <DatePicker label={t('Reimb. start date')} 
                                  onChange={onChange}                   
                                  value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(20% - 8px)'}  }} }
                                /> )}
                            />
                            <Controller name='reimbursementFrequency' control={control}                                     
                                render={ ({field: {onChange, value}}) => (
                                  <TextField select onChange={onChange} value={value} sx={{width:'calc(20% - 8px)'}} id="reimbursementFrequency"
                                    label={t('Reimb. frequency')} inputProps={ {readOnly: false}} >
                                    <MenuItem value='012'>{t('Monthly')}</MenuItem>
                                    <MenuItem value='004'>{t('Quarterly')}</MenuItem>
                                    <MenuItem value='002'>{t('Biannual')}</MenuItem>
                                  </TextField>
                                )}
                            />
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={t('Reimb. base amount')} sx={{width:'calc(20% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`reimbursementBaseAmount`}
                                control={control}
                            />
                            <Controller
                                render={({ field: {onChange, onBlur, name, value, ref} }) => {
                                  return (
                                    <NumberFormat    
                                      label={`# ${t('Reimbursement')}`} sx={{width:'calc(20% - 8px)'}} disabled={false}
                                      allowEmptyFormatting={false}
                                      control={control}    
                                      thousandSeparator={true}
                                      decimalScale={2}
                                      onValueChange={ (v) => onChange(v.floatValue) }
                                      defaultValue={value}
                                      value={value}
                                      customInput={TextFieldRight}                            
                                    />
                                  );
                                }}
                                name={`reimbursementCount`}
                                control={control}
                            />
                            <Controller control={control}
                              name='reimbursementEndDate' 
                              render={({ field: { onChange, onBlur, value, ref } }) => (
                                <DatePicker label={t('Reimb. end date')} 
                                  onChange={onChange} disableOpenPicker readOnly                    
                                  value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(20% - 8px)'}  }} }
                                /> )}
                            />                            
                          </Box>                         
                          
                          { displayEnumerationItemDialog && <DialogEnumerationItemForm 
                              {...{open: displayEnumerationItemDialog, 
                                    title: '', 
                                    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
                                    afterSave: async () => {await refetchEnumerationItems()}}} 
                              />
                          }
                      </Stack>                        
                    </Grid>
                    <Grid item xs={12} md={6} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      <Stack flexDirection='column'>                        
                        <Box sx={{ mt: 1, width: '100%' }} >
                          
                        </Box>
                        
                        
                      </Stack>
                    </Grid>     
                                                  
                </Grid>
            </Box>
        </FormProvider> 
  )
}

